/**
 * Per-chip passage binding (the NotebookLM model, list-side numbering stays
 * flat): an inline citation chip knows the STATEMENT it is attached to (the
 * answer text preceding the marker). When its document carries several cited
 * passages, the chip binds to the passage whose quote best supports that
 * statement — hover shows that passage's quote, click opens the viewer there —
 * instead of always jumping to the document's first passage.
 *
 * Scoring mirrors the backend attribution-lite: token overlap between the
 * statement and each locator excerpt, umlaut-folded, with digit-bearing
 * tokens weighted 3x (a matching figure is the strongest support signal).
 */

import { ChatSource, SourceLocator } from '../../api/chatTypes';
import type { PdfPassage } from './PdfPassageDrawer';

// Numbers first, kept whole including German thousands/decimal separators
// ("480.000") — a separator split would yield a digit-weighted "000" token
// that falsely matches every other thousands figure (mirrors the backend
// attribution tokenizer).
const TOKEN_RE = /\d[\d.,]*\d|\d|[a-z0-9äöüß~_/-]{2,}/g;

/** High-frequency glue words (de+en) that carry no binding signal. */
const STOPWORDS = new Set([
  'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einem',
  'einer', 'und', 'oder', 'aber', 'auch', 'auf', 'aus', 'bei', 'mit', 'nach',
  'von', 'vor', 'zu', 'zum', 'zur', 'im', 'in', 'ist', 'sind', 'war', 'wird',
  'werden', 'wurde', 'kann', 'muss', 'soll', 'sich', 'nicht', 'als', 'wie',
  'fuer', 'für', 'dass', 'es', 'am', 'an', 'um', 'durch', 'wenn', 'dann',
  'the', 'a', 'an', 'and', 'or', 'but', 'of', 'to', 'on', 'for', 'is',
  'are', 'was', 'be', 'been', 'with', 'as', 'at', 'by', 'it', 'this', 'that',
]);

function foldUmlauts(text: string): string {
  return text
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
}

/** Tokenize a statement/excerpt for overlap scoring. */
export function bindingTokens(text: string): Set<string> {
  const out = new Set<string>();
  for (const raw of text.toLowerCase().match(TOKEN_RE) ?? []) {
    if (STOPWORDS.has(raw)) {
      continue;
    }
    if (raw.length < 3 && !/\d/.test(raw)) {
      continue;
    }
    out.add(foldUmlauts(raw));
  }
  return out;
}

/** All cited passages of a source, preferring the full list. */
export function sourceLocators(source: ChatSource): SourceLocator[] {
  if (source.locators && source.locators.length > 0) {
    return source.locators;
  }
  return source.locator ? [source.locator] : [];
}

/**
 * Pick the source's passage that best supports the given statement.
 * Falls back to the first (best-ranked) passage when there is no statement,
 * only one passage, or nothing overlaps.
 */
export function pickLocatorForStatement(
  source: ChatSource,
  statement?: string | null
): SourceLocator | null {
  const locators = sourceLocators(source);
  if (locators.length === 0) {
    return null;
  }
  if (!statement || locators.length === 1) {
    return locators[0];
  }
  const statementSet = bindingTokens(statement);
  if (statementSet.size === 0) {
    return locators[0];
  }
  let best = locators[0];
  let bestScore = 0;
  for (const locator of locators) {
    const excerpt = locator.excerpt;
    if (!excerpt) {
      continue;
    }
    const excerptSet = bindingTokens(excerpt);
    let score = 0;
    for (const token of statementSet) {
      if (excerptSet.has(token)) {
        score += /\d/.test(token) ? 3 : 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      best = locator;
    }
  }
  return best;
}

/**
 * Every highlight window of one locator as viewer passages: one per quote
 * span when the backend attributed several windows, else the single
 * excerpt. `primary` marks the passage the click was about; the others are
 * drawn as secondary frames so the reader still sees every supporting spot.
 */
/**
 * Index of the locator's quote window that best supports the statement
 * (token overlap like pickLocatorForStatement); 0 when nothing overlaps.
 */
export function pickQuoteIndexForStatement(
  locator: SourceLocator | null | undefined,
  statement?: string | null
): number {
  const quotes = locator?.quotes ?? [];
  if (quotes.length <= 1 || !statement) {
    return 0;
  }
  const statementSet = bindingTokens(statement);
  let best = 0;
  let bestScore = 0;
  quotes.forEach((q, idx) => {
    const quoteSet = bindingTokens(q.text);
    let score = 0;
    for (const token of statementSet) {
      if (quoteSet.has(token)) {
        score += /\d/.test(token) ? 3 : 1;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      best = idx;
    }
  });
  return best;
}

export function locatorPassages(
  source: ChatSource,
  locator: SourceLocator | null | undefined,
  label: string | null | undefined,
  primary = true,
  primaryQuoteIndex?: number
): PdfPassage[] {
  const kind = locator?.loc_kind;
  const pages = kind === 'page' || kind === 'slide' || kind === 'region' ? locator?.positions ?? null : null;
  const base = {
    pages,
    regions: locator?.regions ?? null,
    label: label ?? null,
    primary,
  };
  const quotes = locator?.quotes ?? [];
  if (quotes.length > 0) {
    const passages = quotes.map((q, idx) => ({
      ...base,
      primary: primary && (primaryQuoteIndex === undefined || idx === primaryQuoteIndex),
      quote: q.text,
      quotePrefix: q.prefix ?? null,
      quoteSuffix: q.suffix ?? null,
    }));
    // The primary window leads: the viewer jumps to the first passage.
    if (primaryQuoteIndex !== undefined && primaryQuoteIndex > 0 && primaryQuoteIndex < passages.length) {
      const [lead] = passages.splice(primaryQuoteIndex, 1);
      passages.unshift(lead);
    }
    return passages;
  }
  return [
    {
      ...base,
      quote: locator?.excerpt ?? source.excerpt ?? null,
      quotePrefix: locator?.quote_prefix ?? null,
      quoteSuffix: locator?.quote_suffix ?? null,
    },
  ];
}
